/**
 * What a single walkthrough step needs in order to render. The player owns tour
 * flow (next/prev/end, cross-page nav, miss handling) and hands one of these to
 * the renderer per step; a renderer is a thin, stateless-per-step view over
 * whatever the platform draws with.
 */
export interface WalkthroughStepRender {
  /** Popover heading (step title). */
  title: string;
  /** Popover body as authored — raw HTML. Each renderer decides how to consume
   * it: the web renderer sanitizes it, the NativeScript renderer reduces it to
   * plain text (see `walkthroughPlainText`). */
  html?: string;
  /** 0-based index of this step and the tour's total step count (for "n/total"). */
  index: number;
  total: number;
  /** First/last step gate which nav buttons show and whether Next reads "End". */
  isFirst: boolean;
  isLast: boolean;
  /**
   * Whether to render a Next button. False for a "interact with the highlighted
   * control to advance" step (the player wires the anchor's own tap); the End
   * button still shows on the last step regardless.
   */
  showNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onEnd: () => void;
}

/**
 * The platform's walkthrough view layer: spotlight an anchor, show a centered
 * message, tear down.
 *
 * This is the seam that lets one tour state machine drive driver.js on the web
 * and a native overlay on NativeScript. Everything about geometry, chrome, and
 * dismissal gestures lives behind it; nothing about step sequencing does.
 */
export interface IWalkthroughRenderer<TAnchor = unknown> {
  /**
   * Bring `anchor` into view and settle its measurement before it is
   * highlighted. On the web this is a no-op — driver.js scrolls the target
   * itself. On NativeScript it is real work, and skipping it spotlights an
   * offscreen or not-yet-measured frame.
   */
  prepareAnchor(anchor: TAnchor): Promise<void>;

  /** Spotlight an anchor with its step popover. */
  highlightAnchor(anchor: TAnchor, step: WalkthroughStepRender): void;

  /**
   * Show a centered, anchor-less popover — used for a teaching nav step that
   * describes a page transition and has nothing to point at.
   */
  showCentered(step: WalkthroughStepRender): void;

  /** Tear down the current spotlight/popover (tour ended, or moved to a nav hop). */
  destroy(): void;

  /** Whether a spotlight is currently on screen. */
  isActive(): boolean;
}
